import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/services/languageService";
import { Link } from "react-router-dom";

/**
 * Footer组件 - 显示网站底部信息
 * @returns {JSX.Element} Footer组件
 */
const Footer = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  /**
   * 获取考虑当前语言的路径
   * @param {string} path - 目标路径
   * @returns {string} 带语言前缀的路径 (如果不是英语)
   */
  const getLocalizedPath = (path: string): string => {
    if (currentLanguage === 'en' || path.startsWith('#')) { // Don't localize placeholder anchors
      return path;
    }
    return `/${currentLanguage}${path.startsWith('/') ? path : '/' + path}`;
  };

  /**
   * 导航到指定路径，考虑当前语言 (kept for button-based navigation if still needed)
   * @param {string} path - 目标路径
   */
  const navigateTo = (path: string) => {
    navigate(getLocalizedPath(path));
  };

  return (
    <footer className="w-full bg-white shadow-inner border-t border-gray-200 mt-16">
      {/* Sub-Footer Section START */}
      <div className="bg-gray-100 py-10 px-4 md:px-8">
        <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center sm:text-left">
          {/* Tools Column */}
          <div>
            <h3 className="text-lg font-semibold text-purple-700 mb-4">{t('subFooterToolsTitle')}</h3>
            <ul className="space-y-3">
              <li><Link to={getLocalizedPath('/')} className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterRandomPokemonGeneratorLink')}</Link></li>
              <li><Link to={getLocalizedPath('/pokemon-randomizer')} className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterPokemonRandomizerLink')}</Link></li>
              <li><Link to={getLocalizedPath('/which-pokemon-am-i')} className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterWhichPokemonAmILink')}</Link></li>
            </ul>
          </div>

          {/* Pokémon Database Column */}
          <div>
            <h3 className="text-lg font-semibold text-purple-700 mb-4">{t('subFooterPokemonDatabaseTitle')}</h3>
            <ul className="space-y-3">
              <li><Link to={getLocalizedPath('/pokedex')} className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('pokedexPageTitle')}</Link></li>
              <li><Link to={getLocalizedPath('/type-charts')} className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterTypeChartsLink')}</Link></li>
              <li><a href="#move-list-placeholder" className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterMoveListLink')}</a></li>
            </ul>
          </div>

          {/* Blog Column */}
          <div>
            <h3 className="text-lg font-semibold text-purple-700 mb-4">{t('subFooterBlogTitle')}</h3>
            <ul className="space-y-3">
              <li><a href="#blog-placeholder" className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterLatestArticlesLink')}</a></li>
              <li><a href="#blog-guides-placeholder" className="text-gray-700 hover:text-purple-600 text-sm transition-colors duration-150">{t('subFooterGuidesTutorialsLink')}</a></li>
            </ul>
          </div>
        </div>
      </div>
      {/* Sub-Footer Section END */}

      {/* Existing Footer Content START */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center text-center">
          {/* Logo Section */}
          <div className="flex items-center gap-2 mb-4 cursor-pointer" onClick={() => navigateTo('/')}>
            <img 
              src="/st-pokemon-ball.svg" 
              alt={t("subFooterRandomPokemonGeneratorLink")}
              className="w-6 h-6"
            />
            <span className="text-sm font-medium text-purple-700">{t('title')}</span>
          </div>
          
          {/* Copyright Section */}
          <p className="text-sm text-gray-700 mb-4">{t('copyright')}</p>
          <p className="text-sm text-gray-600 mb-6">{t('trademark')}</p>
          
          {/* Links Section */}
          <div className="flex flex-wrap justify-center space-x-4 sm:space-x-6 mt-2">
            <button 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 sm:mb-0"
              onClick={() => navigateTo('/about-us')}
            >
              {t('aboutUs')}
            </button>
            
            <button 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 sm:mb-0"
              onClick={() => navigateTo('/contact-us')}
            >
              {t('contactUs')}
            </button>
            
            <button 
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              onClick={() => navigateTo('/privacy-policy')}
            >
              {t('privacyPolicy')}
            </button>
          </div>
        </div>
      </div>
      {/* Existing Footer Content END */}
    </footer>
  );
};

export default Footer;
